import Link from "next/link";
import React from "react";
import SiteShell from "@/components/SiteShell";
import { BLOG_POSTS } from "@/lib/blogPosts";

/** Engineering blog hub (www.frazzlecrm.com/blog) — indexable. */
export default function BlogIndex() {
  return (
    <SiteShell
      title="FrazzleCRM engineering blog — how the map-first CRM is built"
      description="Engineering write-ups from the team (and the AI agent) building FrazzleCRM: MCP servers in Next.js, two-way map/automation sync, and the design decisions behind a CRM where the diagram is the automation."
    >

      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-4xl px-6 pb-10 pt-16">
          <div className="mb-4 w-fit rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-[12px] font-semibold text-indigo-700">
            Engineering blog
          </div>
          <h1 className="max-w-3xl text-[34px] font-extrabold leading-[1.15] tracking-tight text-slate-900 sm:text-[42px]">
            How the map-first CRM is built
          </h1>
          <p className="mt-4 max-w-2xl text-[15.5px] leading-relaxed text-slate-600">
            Deep technical write-ups from building FrazzleCRM — the real
            architecture, the bugs that shaped the rules, and nothing
            ghost-written by marketing. Some posts are written by the{" "}
            <Link
              href="/building-in-public"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              AI agent that runs our growth
            </Link>
            ; we say so when they are.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-10">
        <div className="space-y-5">
          {BLOG_POSTS.map((p) => (
            <Link
              key={p.slug}
              href={`/blog/${p.slug}`}
              className="group block rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition hover:border-indigo-200 hover:shadow-md"
            >
              <div className="text-[12.5px] font-medium text-slate-400">
                {p.dateDisplay} · {p.readMinutes} min read
              </div>
              <h2 className="mt-1.5 text-[19px] font-bold leading-snug text-slate-900 group-hover:text-indigo-700">
                {p.title}
              </h2>
              <p className="mt-2 text-[14px] leading-relaxed text-slate-600">
                {p.description}
              </p>
              <div className="mt-3 text-[13.5px] font-semibold text-indigo-600">
                Read the post <span aria-hidden>→</span>
              </div>
            </Link>
          ))}
        </div>

        <p className="mt-10 text-[13.5px] leading-relaxed text-slate-500">
          Looking for definitions rather than architecture? The{" "}
          <Link
            href="/learn"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            CRM and automation glossary
          </Link>{" "}
          covers the concepts these posts assume — triggers, enrollment, goals,
          stop tags, send windows and the rest. For what actually shipped and
          when, the{" "}
          <Link
            href="/changelog"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            changelog
          </Link>{" "}
          is generated from git — every release, including the unglamorous ones.
        </p>
      </section>
    </SiteShell>
  );
}
